/**
 * Dashboard data hooks
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useFirm } from '../../../hooks/use-firm'
import { useAuth } from '../../../contexts/auth-context'
import { queryKeys } from '../../../lib/query-keys'
import type { DashboardMetrics, RiskScore, Notification } from '../types/dashboard.types'

export function useDashboardMetrics() {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.metrics(firmId!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('dashboard_metrics')
        .select('*')
        .eq('firm_id', firmId)
        .single()

      if (error) throw error
      return data as DashboardMetrics
    },
    enabled: !!firmId,
    staleTime: 5 * 60 * 1000,  // 5 min for dashboard
  })

  return { metrics: data ?? null, loading: isLoading, error: error as Error | null }
}

export function useRiskScores(entityType?: 'client' | 'engagement' | 'task') {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.riskScores(firmId!, entityType),
    queryFn: async () => {
      let query = (supabase as any)
        .from('risk_scores')
        .select('*')
        .eq('firm_id', firmId)
        .order('score', { ascending: false })

      if (entityType) {
        query = query.eq('entity_type', entityType)
      }

      const { data, error } = await query

      if (error) throw error
      return (data || []) as RiskScore[]
    },
    enabled: !!firmId,
  })

  return { riskScores: data ?? [], loading: isLoading, error: error as Error | null }
}

export function useNotifications() {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.notifications(firmId!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('notifications')
        .select('*')
        .eq('firm_id', firmId)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return (data || []) as Notification[]
    },
    enabled: !!firmId,
  })

  return { notifications: data ?? [], loading: isLoading, error: error as Error | null }
}

/** Tasks assigned to the current user — for staff dashboard widget */
export function useMyTasks() {
  const { firmId } = useFirm()
  const { user } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', firmId, 'my-tasks', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, name, status, due_date, engagement_id')
        .eq('firm_id', firmId as string)
        .eq('assigned_to', user!.id)
        .not('status', 'in', '("completed","approved")')
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(10)

      if (error) throw error
      return (data || []) as Array<{
        id: string
        name: string
        status: string
        due_date: string | null
        engagement_id: string
      }>
    },
    enabled: !!firmId && !!user?.id,
  })

  return { tasks: data ?? [], loading: isLoading }
}

/** Pending approvals for manager/partner queue */
export function usePendingApprovals() {
  const { firmId } = useFirm()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', firmId, 'pending-approvals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('approvals')
        .select('id, status, review_level, created_at, task_id, engagement_id')
        .eq('firm_id', firmId as string)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(10)

      if (error) throw error
      return (data || []) as Array<{
        id: string
        status: string
        review_level: string
        created_at: string
        task_id: string | null
        engagement_id: string | null
      }>
    },
    enabled: !!firmId,
    staleTime: 2 * 60 * 1000,
  })

  return { approvals: data ?? [], loading: isLoading }
}

/** Top risk scores for partner view */
export function useTopRiskScores() {
  const { firmId } = useFirm()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', firmId, 'top-risks'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('risk_scores')
        .select('id, entity_type, entity_id, score, risk_factors, calculated_at')
        .eq('firm_id', firmId)
        .gte('score', 50)
        .order('score', { ascending: false })
        .limit(5)

      if (error) throw error
      return (data || []) as RiskScore[]
    },
    enabled: !!firmId,
    staleTime: 5 * 60 * 1000,
  })

  return { riskScores: data ?? [], loading: isLoading }
}

export function useMarkNotificationRead() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { firmId } = useFirm()
  const queryClient = useQueryClient()

  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      setLoading(true)
      const { error: updateError } = await (supabase as any)
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)

      if (updateError) throw updateError
      setError(null)

      // Invalidate notifications query
      queryClient.invalidateQueries({ queryKey: ['dashboard', firmId, 'notifications'] })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to mark notification as read')
      setError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { markAsRead, loading, error }
}
